import React, { Dispatch, SetStateAction } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { LeadService } from '~/src/services/lead.service';
import PopupContent from '~/src/components/common-components/popup-content/popup-content';
import { createErrorHandler } from '../helper/create-error-handler';
import { onSendSuccessRequestEventAnalytics } from '~/src/repositories/lead/analytics/create-lead-all-referral.analytics';
import { usePopupContent } from '../../../components/common-components/popup-content/use-popup-content.hook';
import { SUCCESS_CODE } from '~/src/models/constants/status-codes';
import { LEAD_REFERRAL_POPUP_DATA, TYPE_ERROR_ACTION } from '~/src/repositories/lead/constants';
import { DEFAULT_ERROR } from '~/src/models/constants/bad-request-errors.constants';
import { MainButtonVisualEnum } from '~/src/components/form-components/main-button/main-button-visual.enum';
import { getAllReferralErrorMapping } from '../constants/error-mapping/all-referral';
import type { ExtendedAxiosResponse, InternalDataFieldInterface } from '~/src/models/types/axios-response.types';
import type { ChipsDataInterface } from '~/src/models/types/chips.types';
import type { CreateLeadAllProductsInterface } from '~/src/repositories/lead/types/lead.types';
import type { FieldError } from '~/src/repositories/lead/types/error-handlers.types';

const QUERY_KEY = 'post create lead all referral';

export const useFetchCreateLeadAllReferral = (
    setChipsData: Dispatch<SetStateAction<ChipsDataInterface>>,
    onSuccess: () => void,
    onError: Dispatch<React.SetStateAction<boolean>>,
    setFieldError?: (fieldError: FieldError) => void,
) => {
    const queryClient = useQueryClient();
    const { resetPopup, buttonCallback, showPopup } = usePopupContent();

    const {
        mutateAsync: createLeadAllReferral,
        isLoading,
    } = useMutation(QUERY_KEY, (data: CreateLeadAllProductsInterface) => LeadService.createLeadReferral(data), {
        onSuccess: (data?: ExtendedAxiosResponse<CreateLeadAllProductsInterface>) => {
            if (data?.response?.status === SUCCESS_CODE || data?.status === SUCCESS_CODE) {
                onSendSuccessRequestEventAnalytics();
                showPopup(
                    <PopupContent
                        {...LEAD_REFERRAL_POPUP_DATA}
                        subButtonAction={buttonCallback}
                        popupId={'lead create all referral success'}
                        resetPopup={resetPopup}
                        closeCallback={buttonCallback}
                        subButtonType={MainButtonVisualEnum.secondary}
                        gapContainer={20}
                    />,
                );
                onSuccess();

                return;
            }

            setChipsData({ error: DEFAULT_ERROR, show: true });
        },
        onError: (error: ExtendedAxiosResponse<InternalDataFieldInterface>) => {
            return createErrorHandler(getAllReferralErrorMapping(onSuccess), {
                showPopup: (popup) => {
                    onError(true);
                    showPopup(popup);
                },
                resetPopup: () => {
                    onError(false);
                    resetPopup();
                },
                subButtonAction: () => {
                    onError(false);
                    buttonCallback();
                },
                closeCallback: buttonCallback,
                setChipsData,
                queryKey: QUERY_KEY,
                setFieldError,
                fallback: () => ({
                    action: {
                        type: TYPE_ERROR_ACTION.chips,
                        error: DEFAULT_ERROR,
                    },
                    effects: {
                        captureException: true,
                        afterAction: buttonCallback,
                    },
                }),
            })(error);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(QUERY_KEY);
        },
    });

    return { createLeadAllReferral, isLoading };
};
